import React, { useEffect, useRef, useState } from 'react';
import { portalCopy } from '../copy';
import type { PartnerBusiness } from '../types';
import { formatListingDate, getListingValidUntil } from '../utils/listingPeriod';
import {
  canCancelSubscription,
  isSubscriptionCancelled,
} from '../utils/subscription';
import { PortalButton } from './PortalUi';

type PartnerSubscriptionMenuProps = {
  business: PartnerBusiness;
  onCancel: () => void;
};

const PartnerSubscriptionMenu: React.FC<PartnerSubscriptionMenuProps> = ({ business, onCancel }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const validUntil = getListingValidUntil(business);
  const validUntilLabel = validUntil ? formatListingDate(validUntil) : null;
  const cancelled = isSubscriptionCancelled(business);
  const canCancel = canCancelSubscription(business);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <PortalButton variant="secondary" onClick={() => setOpen((value) => !value)}>
        {portalCopy.business.manageSubscription}
      </PortalButton>

      {open && (
        <div
          className="absolute right-0 z-20 mt-2 w-[min(100vw-2.5rem,320px)] rounded-[20px] border border-[rgba(74,33,71,0.12)] bg-white p-4 shadow-[0_12px_40px_rgba(74,33,71,0.12)]"
          role="dialog"
          aria-label={portalCopy.business.subscriptionTitle}
        >
          {validUntilLabel && (
            <p className="font-jakarta text-sm text-[#5a3158]">
              {cancelled
                ? portalCopy.dashboard.subscriptionCancelled(validUntilLabel)
                : portalCopy.dashboard.listingValidUntil(validUntilLabel)}
            </p>
          )}

          {canCancel && (
            <PortalButton
              variant="ghost"
              fullWidth
              className="mt-3 !text-[#5a3158]"
              onClick={() => {
                setOpen(false);
                onCancel();
              }}
            >
              {portalCopy.dashboard.cancelSubscription}
            </PortalButton>
          )}
        </div>
      )}
    </div>
  );
};

export default PartnerSubscriptionMenu;
